<template>
  <v-container fluid>
    <!-- Блок фильтров -->
    <v-card flat class="pa-4 mb-6" >
      <div class="mb-2 d-flex align-center">
        <v-icon small class="mr-1">mdi-arrow-left</v-icon>
        <router-link
          :to="`/${i18n().locale}/npa/zakonoproekty`"
        >
          {{$t('Законопроекты на портале')}}
        </router-link>
      </div>
      <v-row align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            label="Ключевое слово и номер регистрации"
            outlined
            hide-details
            dense
            @keyup.enter="applyFilters"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formattedDateRange"
                label="Дата регистрации"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                hide-details
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>

            <v-date-picker
              v-model="filters.date"
              range
              no-title
              scrollable
              @change="handleDateChange"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.initiators"
            :items="initiatorsList"
            label="Инициаторы"
            outlined
            hide-details
            dense
            multiple
            clearable
            @change="applyFilters"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            block
            color="#1A3382"
            dark
            height="40"
            depressed
            @click="clearFilters"
          >
            ОЧИСТИТЬ
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Таблица данных -->
    <v-card flat>
      <v-simple-table>
        <template v-slot:default>
          <thead>
          <tr>
            <th class="text-uppercase font-weight-bold blue--text text--darken-4">Номер регистрации</th>
            <th class="text-uppercase font-weight-bold blue--text text--darken-4">Название</th>
            <th class="text-uppercase font-weight-bold blue--text text--darken-4">Инициаторы</th>
            <th class="text-uppercase font-weight-bold blue--text text--darken-4">Дата публикации</th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="loading">
            <td colspan="4" class="text-center py-10">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </td>
          </tr>
          <tr v-else v-for="law in laws" :key="law.id">
            <td class="py-4 text-no-wrap">{{ law.registrationNumber || '—' }}</td>
            <td class="py-4">
              <router-link
                :to="{ name: 'LawDetail', params: { id: law.id } }"
              >
                {{ i18n().locale === 'ru' ? law.titleRu : law.titleKg }}
              </router-link>
            </td>
            <td class="py-4">
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="deputy in law.deputies"
                  :key="deputy.id"
                  small
                  label
                  class="mr-1 mb-1"
                >
                  {{ deputy.lastName }} {{ deputy.firstName }} {{ deputy.surname }}
                </v-chip>
              </div>
            </td>
            <td class="py-4 text-no-wrap">{{ formatDate(law.publishDate) }}</td>
          </tr>
          <tr v-if="!loading && laws.length === 0">
            <td colspan="4" class="text-center py-4">Данные не найдены</td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <div class="py-4 px-4">Источник данных: <a href="https://kenesh.kg/">сайт Жогорку Кенеша Кыргызской Республики</a></div>
      <!-- Пагинация -->
      <v-pagination
        v-model="page"
        :length="totalPages"
        :total-visible="7"
        color="#1A3382"
        @input="onPageChange"
      ></v-pagination>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';
import i18n from './../plugins/i18n';

export default {
  data: () => ({
    laws: [],
    loading: false,
    page: 1,
    totalPages: 0,
    limit: 10,
    dateMenu: false,
    filters: {
      search: '',
      date: [],
      initiators: null
    },
    initiatorsList: []
  }),
  mounted() {
    this.fetchLaws();
  },
  computed: {
    formattedDateRange() {
      if (!this.filters.date || this.filters.date.length !== 2) return '';

      return `${this.filters.date[0]} - ${this.filters.date[1]}`;
    }
  },
  methods: {
    i18n() {
      return i18n
    },
    async fetchLaws() {
      this.loading = true;
      try {
        const apiPage = this.page - 1;

        const params = {
          page: apiPage,
          limit: this.limit,
          sortBy: 'publishDate',
          sortDirection: 'desc',
          lang: i18n.locale
        };

        if (this.filters.search) params.query = this.filters.search;
        if (this.filters.date?.length === 2) {
          params.from = this.filters.date[0] + 'T00:00:00';
          params.to = this.filters.date[1] + 'T00:00:00';
        }

        if (this.filters.initiators && this.filters.initiators.length) {
          params.deputies = this.filters.initiators.join(',');
        }
        const response = await axios.get(`${i18n.locale}/api/jk-laws`, { params });

        this.laws = response.data.content;
        this.totalPages = response.data.totalPages;

        if (this.initiatorsList.length === 0) {
          this.extractInitiators(this.laws);
        }
      } catch (error) {
        console.error("Ошибка загрузки:", error);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.fetchLaws();
    },
    clearFilters() {
      this.filters = {
        search: '',
        date: null,
        initiators: null
      };
      this.applyFilters();
    },
    onPageChange() {
      this.fetchLaws();
      window.scrollTo(0, 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    extractInitiators(laws) {
      const deputiesMap = new Map();
      laws.forEach(law => {
        law.deputies.forEach(d => {
          deputiesMap.set(d.id, `${d.lastName} ${d.firstName}`);
        });
      });
      this.initiatorsList = Array.from(deputiesMap).map(([value, text]) => ({ value, text }));
    },
    handleDateChange() {
      if (this.filters.date.length === 2) {
        this.dateMenu = false;
        this.applyFilters();
      }
    }
  }
};
</script>
